import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { Card, ErrorBox, Loading, PageTitle, Stat } from "./ui";
import { inr } from "../lib/format";

const CopyField = ({ label, value, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div>
      <p className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">{label}</p>
      <div className="mt-2 flex items-center gap-2 rounded-xl border border-white/10 bg-[#0B0B0F] px-4 py-3">
        <span className="min-w-0 flex-1 truncate font-mono text-sm text-[#F3E5AB]" data-testid={testId}>{value}</span>
        <button onClick={copy} data-testid={`${testId}-copy`} className="shrink-0 p-1.5 text-[#99A1AF] transition-colors hover:text-white" aria-label={`Copy ${label}`}>
          {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export default function Referrals() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [applyState, setApplyState] = useState({ state: "idle", message: "" });

  useEffect(() => {
    api.get("/referral/stats")
      .then((r) => setStats(r.data))
      .catch(() => setError("Could not load your referral stats. Please refresh."));
  }, []);

  if (error) return <ErrorBox message={error} />;
  if (!stats) return <Loading label="Loading referrals" />;

  const link = `${window.location.origin}/login?ref=${stats.referral_code}`;

  const applyCode = async (e) => {
    e.preventDefault();
    setApplyState({ state: "sending", message: "" });
    try {
      await api.post(`/auth/apply-referral?referral_code=${encodeURIComponent(code)}`);
      setApplyState({ state: "done", message: "Referral code applied to your account." });
      await refresh();
    } catch (err) {
      setApplyState({ state: "error", message: err.response?.data?.detail || "Could not apply that code." });
    }
  };

  return (
    <div data-testid="referrals-page">
      <PageTitle title="Referrals" sub="Earn with your community — 25% referral commission." />

      <Card className="mt-8 border-[#D4AF37]/25" data-testid="referral-code-card">
        <div className="grid gap-5 sm:grid-cols-2">
          <CopyField label="Your Referral Code" value={stats.referral_code || "—"} testId="referral-code" />
          <CopyField label="Your Referral Link" value={link} testId="referral-link" />
        </div>
        <p className="mt-4 text-xs leading-relaxed text-[#636B78]">
          Share your link — new members who sign up through it are linked to your account automatically.
        </p>
      </Card>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat testId="referral-total" label="Total Referrals" value={stats.total_referrals} />
        <Stat gold testId="referral-earned" label="Total Earned" value={inr(stats.referral_bonus_earned)} />
        <Stat testId="referral-available" label="Available" value={inr(stats.referral_bonus_available)} sub={`Min withdrawal ${inr(stats.min_withdrawal_inr)}`} />
        <Stat testId="referral-withdrawn" label="Withdrawn" value={inr(stats.referral_bonus_withdrawn)} />
      </div>

      <Card className="mt-6" data-testid="referral-commission-card">
        <p className="text-sm text-[#99A1AF]">
          Commission rate: <span className="font-display text-lg font-bold text-gold-gradient">{stats.referral_percent}%</span>
          <span className="text-[#636B78]"> on eligible purchases made by your referrals.</span>
        </p>
        <p className="mt-3 text-[11px] leading-relaxed text-[#636B78]">
          A per-referral history list is not exposed by the current API — aggregate earnings are shown above.
        </p>
      </Card>

      {!user?.has_referrer && (
        <Card className="mt-6" data-testid="apply-referral-card">
          <h2 className="font-display text-lg font-semibold text-white">Have a referral code?</h2>
          <form onSubmit={applyCode} className="mt-4 flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              data-testid="apply-referral-input"
              className="w-full max-w-xs rounded-xl border border-white/10 bg-[#0B0B0F] px-4 py-3 font-mono text-sm tracking-widest text-white placeholder:text-[#3A3F4A] outline-none focus:border-[#D4AF37]/60"
            />
            <button
              type="submit"
              disabled={applyState.state === "sending" || code.length < 3}
              data-testid="apply-referral-button"
              className="rounded-full border border-[#D4AF37]/40 px-6 py-3 text-sm font-medium text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-40"
            >
              {applyState.state === "sending" ? "Applying…" : "Apply Code"}
            </button>
          </form>
          {applyState.message && (
            <p className={`mt-3 text-sm ${applyState.state === "error" ? "text-red-400" : "text-emerald-400"}`} data-testid="apply-referral-status">
              {applyState.message}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
